import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
    Header, Button, Form, Input, TextArea,
    Divider, Label, Message, Loader, Container
} from 'semantic-ui-react';

import { MediaTile } from '../../../components/MediaTile';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import { StreamBadge, STREAM_BADGE_FIELDS } from '../../../components/StreamBadge';
import { PublishToStream } from './PublishToStream';
import { AdvertisedRoles } from '../../../components/AdvertisedRoles';
import { PollPreview } from '../../../components/PollPreview';

// Post is a union (CommunityUpdate | CommunityPrayerRequest | LeadershipUpdate
// | LeadershipPrayerRequest | Birthday); fields must be selected per member.
const POST_SENT_FIELDS = `
    id
    title
    description
    type
    publishAt
    archivedAt
    canEditText
    repostedStreamIds
    ${STREAM_BADGE_FIELDS}
    images { id url contentType durationSeconds }
`;

const GET_SENT_POST = gql`
     query SentPost($postId: Int!) {
         sentPost(id: $postId) {
             ... on CommunityUpdate { ${POST_SENT_FIELDS} }
             ... on VolunteerCall { ${POST_SENT_FIELDS} advertisedVolunteerRoles { id title type } }
             ... on Poll { ${POST_SENT_FIELDS} options { id label } allowMultiple allowOther closesAt }
             ... on CommunityPrayerRequest { ${POST_SENT_FIELDS} }
             ... on LeadershipUpdate { ${POST_SENT_FIELDS} }
             ... on LeadershipPrayerRequest { ${POST_SENT_FIELDS} }
             ... on Birthday { ${POST_SENT_FIELDS} }
         }
     }
`;

const UPDATE_SENT_POST = gql`
    mutation UpdateSentPost($id: Int!, $title: String!, $description: String!) {
        updateSentPost(id: $id, title: $title, description: $description) {
            ... on CommunityUpdate { id }
            ... on VolunteerCall { id }
            ... on Poll { id }
            ... on CommunityPrayerRequest { id }
            ... on LeadershipUpdate { id }
            ... on LeadershipPrayerRequest { id }
            ... on Birthday { id }
        }
    }
`;

/**
 * Pulling a published post, and putting it back.
 *
 * These used to live only in the "Recently Sent" sidebar on the scheduled-posts page. That page
 * is now a per-stream timeline with no sidebar, so without moving them there would be no way at
 * all to take down a post that has gone out. They belong here anyway: taking something down is
 * a decision about one specific post, made while looking at it.
 */
const TAKEDOWN_SENT_POST = gql`
    mutation TakedownSentPost($id: Int!) {
        takedownSentPost(id: $id) {
            ... on CommunityUpdate { id archivedAt }
            ... on VolunteerCall { id archivedAt }
            ... on Poll { id archivedAt }
            ... on CommunityPrayerRequest { id archivedAt }
            ... on LeadershipUpdate { id archivedAt }
            ... on LeadershipPrayerRequest { id archivedAt }
            ... on Birthday { id archivedAt }
        }
    }
`;

const RESTORE_SENT_POST = gql`
    mutation RestoreSentPost($id: Int!) {
        restoreSentPost(id: $id) {
            ... on CommunityUpdate { id archivedAt }
            ... on VolunteerCall { id archivedAt }
            ... on Poll { id archivedAt }
            ... on CommunityPrayerRequest { id archivedAt }
            ... on LeadershipUpdate { id archivedAt }
            ... on LeadershipPrayerRequest { id archivedAt }
            ... on Birthday { id archivedAt }
        }
    }
`;

type EditableFields = { title: string; description: string };

// --- Component ---

const PostSentView: React.FC = () => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const [isExiting, setIsExiting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const { data, loading, error, refetch } = useQuery(GET_SENT_POST, {
        variables: { postId: Number(postId) },
        skip: !postId,
        // Editability is time-window-based, so a cached "not editable" from a previous visit
        // must not linger — always re-check against the server.
        fetchPolicy: 'network-only',
    });

    // Seeded once from the query result, then edited locally until Save.
    const [form, setForm] = useState<EditableFields | null>(null);
    useEffect(() => {
        if (data?.sentPost && form == null) {
            setForm({
                title: data.sentPost.title || '',
                description: data.sentPost.description || '',
            });
        }
    }, [data, form]);

    const [updatePost, { loading: saving }] = useMutation(UPDATE_SENT_POST);
    const [takedownPost, { loading: takingDown }] = useMutation(TAKEDOWN_SENT_POST);
    const [restorePost, { loading: restoring }] = useMutation(RESTORE_SENT_POST);

    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    const handleSave = async () => {
        if (!form) return;
        setActionError(null);
        try {
            await updatePost({
                variables: {
                    id: Number(postId),
                    title: form.title,
                    description: form.description,
                }
            });
            setIsExiting(true);
            setTimeout(() => navigate(PATHS.POSTS_SCHEDULED), 500);
        } catch (err: any) {
            setActionError(err?.message || 'Could not save changes.');
        }
    };

    // Gated on `loading` alone — `form` never gets set when `sentPost` legitimately resolves
    // to null, so including it here would spin this loader forever instead of ever reaching
    // the messages below.
    if (loading) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading post...</Loader>;

    if (error) {
        return (
            <Container text style={{ padding: '3em 0em' }}>
                <Message error content={error.message || 'Could not load this post.'} />
            </Container>
        );
    }

    // `sentPost` no longer hides posts past the edit window — it returns them, and `canEditText`
    // says whether the text is still changeable. So a null here really does mean "no such post
    // in this organisation", and the message can say that instead of guessing at the age.
    if (!data?.sentPost) {
        return (
            <Container text style={{ padding: '3em 0em' }}>
                <Message error content="That post could not be found." />
            </Container>
        );
    }

    // `form` is seeded from `data.sentPost` by the effect above, one render after data lands.
    if (!form) return <Loader active inline='centered' style={{ marginTop: '5em' }}>Loading post...</Loader>;

    const { sentPost: post } = data;
    const publishDate = parseApiDate(post.publishAt);
    const overLimit = form!.title.length > POST_TITLE_LIMIT || form!.description.length > POST_DESCRIPTION_LIMIT;
    // Past the window the text is frozen, but the post is not: republishing it to another
    // stream, taking it down and putting it back all stay available below.
    const canEdit = post.canEditText !== false;

    return (
        <Container
            text
            style={{
                padding: '3em 0em',
                transition: 'opacity 0.5s ease-in-out',
                opacity: isExiting ? 0 : 1
            }}
        >
            <Label basic color="grey" size="small" style={{ marginBottom: '1em' }}>{post.type}</Label>
            <AdvertisedRoles roles={post.advertisedVolunteerRoles} />
            <PollPreview post={post} />
            <div style={{ marginBottom: '10px' }}>
                <StreamBadge stream={post.stream} size="small" />
            </div>
            <Header as="h1" style={{ marginTop: 0 }}>Edit sent post</Header>
            <p style={{ color: '#666' }}>
                Published {publishDate?.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.
                {canEdit
                    ? ' Title and description can be corrected for up to 2 days after publishing.'
                    : ' Past the 2-day correction window, so the text is fixed — but it can still be published to another stream, or taken down.'}
            </p>

            {actionError && <Message error content={actionError} />}

            {post.images.length > 0 && (
                <div style={{ display: 'flex', overflowX: 'auto', gap: '12px', paddingBottom: '15px', marginBottom: '2em' }}>
                    {post.images.map((img: any) => (
                        <MediaTile
                            key={img.id}
                            url={img.url}
                            contentType={img.contentType}
                            durationSeconds={img.durationSeconds}
                            rounded
                            bordered
                            controls
                            style={{ height: '180px', objectFit: 'cover' }}
                        />
                    ))}
                </div>
            )}

            <Form>
                <Form.Field required>
                    <label>Title</label>
                    <Input
                        value={form!.title}
                        disabled={saving || !canEdit}
                        readOnly={!canEdit}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, title: value }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form!.title.length, POST_TITLE_LIMIT) }}>
                        {form!.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required={post.type !== 'POLL'}>
                    <label>{post.type === 'POLL' ? 'More detail (optional)' : 'Description'}</label>
                    <TextArea
                        rows={6}
                        value={form!.description}
                        disabled={saving || !canEdit}
                        readOnly={!canEdit}
                        onChange={(_, { value }) => setForm(f => f && ({ ...f, description: value as string }))}
                    />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(form!.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {form!.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>
            </Form>

            <Divider style={{ margin: '3em 0 2em 0' }} />

            {/* No Save past the window: `updateSentPost` re-checks it and would refuse, so a
                button here could only ever produce an error. */}
            {canEdit && (
                <Button
                    positive
                    size="huge"
                    fluid
                    icon="checkmark"
                    labelPosition="left"
                    content="Save changes"
                    loading={saving}
                    disabled={isExiting || saving || !form!.title || (!form!.description && post.type !== 'POLL') || overLimit}
                    onClick={handleSave}
                />
            )}

            {/* The thing that is still worth doing with an old post, and the reason this page
                now loads one at all. Hidden once taken down — republishing something you have
                just pulled from the feed is almost certainly not the intent. */}
            {post.archivedAt == null && (
                <>
                    <Divider style={{ margin: '2.5em 0 1.5em 0' }} />
                    <PublishToStream
                        postId={Number(postId)}
                        currentStreamId={post.stream?.id}
                        repostedStreamIds={post.repostedStreamIds ?? []}
                        sourcePublishAt={publishDate}
                        onPublished={refetch}
                    />
                </>
            )}

            {/* Taking a post down hides it from every reader; it is not a delete, and the copy
                stays recoverable, which is why the restore sits in the same place. */}
            <Divider style={{ margin: '2.5em 0 1.5em 0' }} />

            {post.archivedAt != null ? (
                <>
                    <Message
                        warning
                        size="small"
                        content="This post has been taken down. Nobody can see it in their feed."
                    />
                    <Button
                        basic
                        color="green"
                        icon="undo"
                        labelPosition="left"
                        content="Put it back"
                        loading={restoring}
                        disabled={restoring}
                        onClick={async () => {
                            setActionError(null);
                            try {
                                await restorePost({ variables: { id: Number(postId) } });
                                await refetch();
                            } catch {
                                setActionError('Could not restore that post.');
                            }
                        }}
                    />
                </>
            ) : (
                <Button
                    basic
                    color="red"
                    icon="ban"
                    labelPosition="left"
                    content="Take this post down"
                    loading={takingDown}
                    disabled={takingDown}
                    onClick={async () => {
                        setActionError(null);
                        try {
                            await takedownPost({ variables: { id: Number(postId) } });
                            await refetch();
                        } catch {
                            setActionError('Could not take that post down.');
                        }
                    }}
                />
            )}
        </Container>
    );
};

export { PostSentView };
